import { memo } from "react";
import { Message } from "../types";
import { useChat } from "../utils/chatContext";
import { Markdown } from "./Markdown";
import logoUrl from "../assets/ctsi-buffalo-logo.png";

export function ChatList({ messages }: { messages: Message[] }) {
  const { debug } = useChat();

  if (messages.length === 0) {
    return null;
  }

  let displayMessages: Message[];

  if (debug) {
    displayMessages = messages;
  } else {
    const filtered = messages.filter((m) => {
      if (m.role === "tool") return false;
      if (
        m.role === "assistant" &&
        m.toolCalls &&
        m.toolCalls.length > 0 &&
        !m.content?.trim()
      )
        return false;
      if (
        m.role === "assistant" &&
        !m.isStreaming &&
        !m.content?.trim() &&
        !m.toolCalls?.length
      )
        return false;
      return true;
    });

    displayMessages = [];
    for (const msg of filtered) {
      const prev = displayMessages[displayMessages.length - 1];
      if (
        msg.role === "assistant" &&
        prev?.role === "assistant" &&
        !prev.isStreaming &&
        msg.content
      ) {
        displayMessages[displayMessages.length - 1] = {
          ...prev,
          content: prev.content + "\n\n" + msg.content,
          isStreaming: msg.isStreaming,
          timestamp: msg.timestamp,
        };
      } else {
        displayMessages.push(msg);
      }
    }
  }

  return (
    <div className="space-y-5">
      {displayMessages.map((msg) => (
        <MessageRow key={msg.id} message={msg} debug={debug} />
      ))}
    </div>
  );
}

const TOOL_LABELS: Record<string, string> = {
  search_ctsi: "Searching CTSI pages",
  search_ctsi_staff: "Searching CTSI staff",
  search_ub_experts: "Finding UB experts",
  search_clinical_trials: "Searching clinical trials",
  echo: "Echo",
};

function toolLabel(name: string): string {
  return TOOL_LABELS[name] ?? name;
}

const MessageRow = memo(function MessageRow({
  message,
  debug,
}: {
  message: Message;
  debug: boolean;
}) {
  const isUser = message.role === "user";

  // ---- Tool result (debug) ----
  if (message.role === "tool") {
    return (
      <div className="flex justify-start message-enter">
        <details className="group max-w-[85%] rounded-xl border border-slate-200 bg-white overflow-hidden">
          <summary className="flex items-center gap-2 px-3.5 py-2 text-xs text-slate-500 cursor-pointer hover:bg-slate-50 select-none">
            <svg
              className="w-3.5 h-3.5 text-slate-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.8}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
            <span className="font-medium">
              {message.toolName ?? "tool"} · result
            </span>
            <svg
              className="w-3 h-3 ml-auto transition-transform group-open:rotate-180"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 8.25l-7.5 7.5-7.5-7.5"
              />
            </svg>
          </summary>
          <pre className="px-3.5 pb-3 pt-1 text-[11px] leading-relaxed text-slate-500 whitespace-pre-wrap break-words font-mono max-h-64 overflow-y-auto chat-scroll border-t border-slate-100">
            {message.content}
          </pre>
        </details>
      </div>
    );
  }

  // ---- Tool call (debug) ----
  if (
    message.role === "assistant" &&
    message.toolCalls &&
    message.toolCalls.length > 0 &&
    !message.content
  ) {
    return (
      <div className="flex justify-start message-enter">
        <div className="max-w-[85%] space-y-1.5">
          {message.toolCalls.map((tc) => (
            <div
              key={tc.id}
              className="rounded-xl border border-amber-200/80 bg-amber-50/60 px-3.5 py-2"
            >
              <div className="flex items-center gap-2 text-xs text-amber-700">
                <svg
                  className="w-3.5 h-3.5 spin"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                  />
                </svg>
                <span className="font-medium">
                  {toolLabel(tc.function.name)}
                </span>
              </div>
              <pre className="mt-1 text-[11px] text-slate-500 whitespace-pre-wrap break-words font-mono">
                {formatArgs(tc.function.arguments)}
              </pre>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ---- User ----
  if (isUser) {
    return (
      <div className="flex justify-end message-enter">
        <div className="max-w-[78%]">
          <div className="rounded-2xl rounded-br-md px-4 py-2.5 bg-[#005bbb] text-white shadow-sm shadow-blue-900/20">
            <div className="text-[15px] leading-relaxed">
              <Markdown content={message.content} dark />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Assistant: flat layout, avatar + content ----
  const isEmpty = !message.content && message.isStreaming;

  return (
    <div className="flex justify-start gap-3 message-enter">
      <img
        src={logoUrl}
        alt="CTSI Buffalo"
        className="w-7 h-7 mt-0.5 shrink-0 rounded-lg shadow-sm"
      />

      <div className="min-w-0 flex-1 pt-0.5">
        {debug && message.reasoning && (
          <details className="mb-2 group">
            <summary className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 cursor-pointer hover:text-slate-500 select-none">
              <svg
                className="w-3 h-3 transition-transform group-open:rotate-90"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8.25 4.5l7.5 7.5-7.5 7.5"
                />
              </svg>
              Reasoning
            </summary>
            <div className="mt-1.5 pl-3 border-l-2 border-slate-200 text-xs text-slate-500 leading-relaxed">
              <Markdown content={message.reasoning} />
            </div>
          </details>
        )}

        {isEmpty ? (
          <div className="flex items-center gap-1.5 py-1.5 text-[#005bbb]/60">
            <span className="typing-dot" />
            <span className="typing-dot" />
            <span className="typing-dot" />
          </div>
        ) : message.isStreaming ? (
          <div className="text-[15px] text-slate-800 leading-relaxed streaming-text">
            {message.content}
          </div>
        ) : (
          <div className="text-[15px] text-slate-800 leading-relaxed">
            <Markdown content={message.content} />
          </div>
        )}
      </div>
    </div>
  );
});

function formatArgs(args: string): string {
  try {
    return JSON.stringify(JSON.parse(args), null, 2);
  } catch {
    return args;
  }
}
